// How to use Ahillah: every word of the guide (Help, "Show me around", "How do I…?", the offer to new users)
// in one place. Whenever the guide names something on the screen it uses N below, exactly as the screen shows it,
// so renaming a button means changing it here once (tests/guide.test.ts checks these names against the screens).

import type { Panel } from '../state';

/** Names on the screen, exactly as they appear. */
export const N = {
  // The dock, left to right. The Point button reads Explore while Ahillah follows the phone. Sky view opens the panel
  // whose id is still 'layers'; More opens every other part of Ahillah.
  search: 'Search',
  tonight: 'Tonight',
  point: 'Point',
  explore: 'Explore',
  layers: 'Sky view',
  more: 'More',
  // In pointing mode: the chip that lines the sky up with the real one, and its card's last tap.
  align: 'Align',
  linedUp: 'Lined up',
  // The top bar: the time pill's way back from another time, the cog and the question mark.
  now: 'Now',
  settings: 'Settings',
  help: 'Help',
  // Inside sheets and cards.
  nightVision: 'Night vision',
  onTheSky: 'On the sky',
  nakedEye: 'Naked eye',
  fullSky: 'Full sky',
  darkCheck: 'Darkness check',
  useMyLocation: 'Use my current location',
  comingUp: 'Coming up',
  calendarLink: 'The year ahead: sky calendar',
  skyCalendar: 'Sky calendar',
  remindMe: 'Remind me',
  listen: 'Listen',
  say: 'Say',
  seenIt: 'Seen it',
  yourNights: 'Your nights',
  gallery: 'Gallery',
  arabicNames: 'Arabic names',
  arabicVoice: 'Arabic voice',
  notifications: 'Notifications',
  backUp: 'Back up your nights',
  restore: 'Restore from a backup',
} as const;

// ---------- Show me around: the real controls, one at a time ----------

export type StepId = 'sky' | 'point' | 'search' | 'tonight' | 'layers' | 'more' | 'place' | 'time' | 'settings' | 'help';

/** How the screen is right now: a few captions depend on it. */
export interface StepContext {
  /** A touch screen (phone or tablet) rather than a mouse. */
  touch: boolean;
  /** Ahillah is following the phone, so the dock's Point button reads Explore. */
  pointing: boolean;
  /** The sky shows the present moment (the time pill has no Now button). */
  live: boolean;
  /** The Help button is in the top bar (on a narrow phone it gives way while another time is shown). */
  helpButton: boolean;
}

export interface Step { id: StepId; title: (c: StepContext) => string; text: (c: StepContext) => string }

/** One sentence each. */
export const STEPS: Step[] = [
  {
    id: 'sky',
    title: () => 'The sky',
    text: (c) => (c.pointing
      ? 'Hold your phone up and the sky follows it; tap anything to see what it is.'
      : c.touch
        ? 'Tap anything to see what it is, drag to look around, and pinch to zoom in.'
        : 'Click anything to see what it is, drag to look around, and scroll to zoom in.'),
  },
  {
    id: 'point',
    title: (c) => (c.pointing ? N.explore : N.point),
    text: (c) => (c.pointing
      ? `Tap ${N.explore} to stop following the phone and move the sky with your finger instead.`
      : c.touch
        ? `Tap ${N.point} and hold your phone up: Ahillah follows wherever you point it.`
        : `On a phone or tablet, tap ${N.point} and hold it up to the sky: Ahillah follows wherever you point it.`),
  },
  { id: 'search', title: () => N.search, text: () => 'Find a planet, star, constellation or satellite by its name.' },
  { id: 'tonight', title: () => N.tonight, text: () => 'See what is up tonight and how clear it will be, with a guided tour and what is coming up.' },
  { id: 'layers', title: () => N.layers, text: () => `Choose what the sky shows, such as constellations or satellites, and switch on ${N.nightVision}.` },
  { id: 'more', title: () => N.more, text: () => `Everything else in one place: lessons, The stars in Islam, the sky calendar, ${N.yourNights} and the ${N.gallery}.` },
  { id: 'place', title: () => 'Your place', text: () => 'Ahillah shows the sky from this place; tap it to choose another.' },
  {
    id: 'time',
    title: () => 'Time',
    text: (c) => (c.live
      ? 'Tap the time to see the sky at another moment, or to speed it up.'
      : `You are seeing another time; tap ${N.now} to come back to the live sky.`),
  },
  { id: 'settings', title: () => N.settings, text: () => 'Turn on notifications, choose Arabic names, and set how dark your sky is.' },
  {
    id: 'help',
    title: () => N.help,
    text: (c) => (c.helpButton
      ? 'Tap here any time to see this tour again, or to find out how to do something.'
      : `This tour, and how to do everyday things, are in ${N.settings} under ${N.help}.`),
  },
];

/** The words around the steps. */
export const WALK = {
  label: 'Show me around',
  progress: (i: number, n: number) => `${i} of ${n}`,
  back: 'Back',
  next: 'Next',
  done: 'Done',
  skip: 'Skip',
};

// ---------- How do I…? ----------

/** Something to light up for a moment once a sheet or card has opened: found by its words or by a selector. */
export interface Flash {
  /** The words it shows, exactly. */
  text?: string;
  /** Or a CSS selector, for something without words (the search box). */
  sel?: string;
  /** The part to light up around those words: the switch's row, the group, the button. */
  row?: string;
}

/** Where "Show me" goes: a step of the tour (a control on the main screen), a sheet, or the card for the Moon. */
export type Show =
  | { step: StepId }
  | { panel: Exclude<Panel, null>; flash?: Flash }
  | { card: 'Moon'; flash?: Flash };

/** `steps`: numbered steps after the answer. `ios`: shown only in Ahillah opened from an iPhone's or iPad's Home Screen. */
export interface Task { id: string; icon: string; title: string; answer: string; steps?: string[]; show: Show; ios?: boolean }

export const TASKS: Task[] = [
  {
    id: 'what',
    icon: 'target',
    title: 'Find out what something is',
    answer: 'Tap it in the sky and its card opens. If you are pointing your phone, put it in the circle in the middle: its name shows at the bottom of the screen.',
    show: { step: 'sky' },
  },
  {
    id: 'planet',
    icon: 'planet',
    title: 'Find a planet',
    answer: `Tap ${N.search}, type its name, such as Jupiter, and choose it. If you are pointing your phone, the line at the top tells you which way to turn. ${N.tonight} lists the planets that are up tonight.`,
    show: { panel: 'search', flash: { sel: '.search-box' } },
  },
  {
    id: 'point',
    icon: 'phone',
    title: 'Point my phone at the sky',
    answer: `Tap ${N.point} and hold your phone up: Ahillah follows wherever you point it. Drag with a finger to look around, and move the phone to carry on following it.`,
    show: { step: 'point' },
  },
  {
    id: 'align',
    icon: 'target',
    title: 'Make the sky match the real one',
    answer: `In pointing mode, tap ${N.align}. Hold your phone up so the circle in the middle covers the Moon, planet or star it names, then tap ${N.linedUp}. It lasts for the night.`,
    show: { step: 'point' },
  },
  {
    id: 'show',
    icon: 'layers',
    title: 'Choose what the sky shows',
    answer: `Tap ${N.layers} and choose from ${N.onTheSky}: constellations, star names, galaxies, satellites and meteor showers, in any mix.`,
    show: { panel: 'layers', flash: { text: N.onTheSky, row: '.group' } },
  },
  {
    id: 'eye',
    icon: 'eye',
    title: 'See just the stars I can see',
    answer: `Tap ${N.layers}, then ${N.nakedEye}: Ahillah shows what your eyes can see from here tonight, with names. To fit it to your eyes, tap ${N.darkCheck} and the faintest ringed star you can see. ${N.fullSky} shows everything.`,
    show: { panel: 'layers', flash: { text: N.nakedEye, row: '.group' } },
  },
  {
    id: 'night',
    icon: 'nightEye',
    title: 'Use night vision',
    answer: `Tap ${N.layers} and switch on ${N.nightVision}, near the top. It warms the screen’s colours so your eyes stay used to the dark. Turn your phone’s brightness down too.`,
    show: { panel: 'layers', flash: { text: N.nightVision, row: '.toggle-row' } },
  },
  {
    id: 'place',
    icon: 'pin',
    title: 'Change place',
    answer: `Tap the place name at the top left, then search for a town or tap ${N.useMyLocation}.`,
    show: { step: 'place' },
  },
  {
    id: 'time',
    icon: 'clock',
    title: 'See the sky at another time',
    answer: `Tap the time at the top to choose a date and time, or to speed the sky up. Tap ${N.now} to come back to the live sky.`,
    show: { step: 'time' },
  },
  {
    id: 'remind',
    icon: 'calendar',
    title: 'Set a reminder for a sky event',
    answer: `Tap ${N.more}, then ${N.skyCalendar}. Open an event and tap ${N.remindMe}.`,
    show: { panel: 'calendar' },
  },
  {
    id: 'listen',
    icon: 'speaker',
    title: 'Listen to facts',
    answer: `Open the card for a planet, star or constellation and tap ${N.listen}. A voice reads a few checked facts about it.`,
    show: { card: 'Moon', flash: { text: N.listen, row: '.info-act' } },
  },
  {
    id: 'log',
    icon: 'nights',
    title: 'Keep a log of what I have seen',
    answer: `When you spot something, open its card and tap ${N.seenIt}. ${N.yourNights}, in ${N.more}, keeps a list for each night.`,
    show: { panel: 'nights' },
  },
  {
    id: 'gallery',
    icon: 'gallery',
    title: 'See the best pictures of space',
    answer: `Tap ${N.more}, then ${N.gallery}. Tap a picture to see it full screen with its story and credit, and swipe for the next.`,
    show: { panel: 'gallery' },
  },
  {
    id: 'arabic',
    icon: 'star',
    title: 'See Arabic names',
    answer: `Cards show the Arabic names from the start. If they are off, open ${N.settings} with the cog at the top right and switch on ${N.arabicNames}. ${N.layers} offers the Arabian star groups and the Moon’s stations.`,
    show: { panel: 'settings', flash: { text: N.arabicNames, row: '.toggle-row' } },
  },
  {
    id: 'alerts',
    icon: 'bell',
    title: 'Get notifications',
    answer: `Open ${N.settings} and switch on ${N.notifications}, for clear evenings, space station passes and sky events. On an iPhone, add Ahillah to your Home Screen first.`,
    show: { panel: 'settings', flash: { text: N.notifications, row: '.toggle-row' } },
  },
  {
    // An iPhone keeps the icon a Home Screen app was added with (WebKit bug 216197), and the new one starts with
    // empty storage, so the nights go across in a backup (the owner, 22 October 2026: DECISIONS 101). The old icon goes
    // last, once the nights are safely in the new one (iOS keeps the two apart). Only in the Home Screen app: followed
    // in a browser, which keeps its own storage, step 1 would back up the wrong nights.
    id: 'icon',
    icon: 'phone',
    title: 'Get the new icon on my iPhone or iPad',
    answer: 'An iPhone or iPad keeps the icon Ahillah had when you first added it, so add Ahillah again. Only your nights come with you: settings, reminders, darkness checks and lessons start afresh.',
    steps: [
      `Open ${N.settings}, tap ${N.backUp}, then Save to Files, then Save. No nights yet? Skip this.`,
      'In your browser, such as Safari or Chrome, go to ahillah.pages.dev.',
      'Tap Share (no Share button? Look in the browser’s menu, often three dots), then Add to Home Screen.',
      'Keep Open as Web App on if you see it, and tap Add. Open Ahillah from its new icon and go through the welcome.',
      `In ${N.settings}, tap ${N.restore}, choose your file, and check your nights are in ${N.yourNights}.`,
      `Now touch and hold the old icon and remove it. If you use ${N.notifications}, switch them on again in ${N.settings}.`,
    ],
    show: { panel: 'settings', flash: { text: N.backUp, row: '.list-row' } },
    ios: true,
  },
];

// ---------- The Help sheet ----------

export const HELP = {
  title: N.help,
  tourTitle: 'Show me around',
  tourText: 'A quick look at each button on the screen, one at a time. It takes about a minute.',
  tourStart: 'Start',
  /** Help's first row (src/ui/FeedbackPanel.tsx): the question mark is on the sky throughout. */
  feedback: 'Something not right? Tell us',
  feedbackSub: 'Or share an idea. We read every message.',
  howTitle: 'How do I…?',
  showMe: 'Show me',
  filmsTitle: 'Films',
  featuresTitle: 'Everything Ahillah can do',
  fine: 'Everything runs on your phone and works offline once installed, apart from live weather and satellite updates.',
  /** The Help row in Settings. */
  settingsSub: 'A tour of the buttons, and how to do everyday things',
};

/** The films, as Help offers them. */
export const FILM_ROWS: { film: 'intro' | 'calendar'; title: string; sub: string }[] = [
  { film: 'intro', title: 'Watch the introduction', sub: 'Ahillah in 65 seconds, with sound.' },
  { film: 'calendar', title: 'Watch: the sky calendar', sub: 'The year ahead in the sky, in 74 seconds, with sound.' },
];

/** Everything Ahillah does, so nothing stays hidden: icon, title, text, and the part of Ahillah it opens. */
export const FEATURES: [string, string, string, Exclude<Panel, null> | null][] = [
  ['book', 'The stars in Islam', `What the Quran and the Sunnah say about the sky, explained by the scholars: the stars as signs of Allah, astrology, shooting stars and the old names in the sky. In ${N.more} and ${N.tonight}.`, 'islam'],
  ['phone', 'Point at the sky', 'Ahillah follows your phone: hold it up and stars, planets, the Moon and satellites are named as you move. Drag with a finger to look around, and it follows the phone again when you aim somewhere new. Tap anything for details.', null],
  ['moon', N.tonight, 'What is up tonight: a score for the night, the chance of clear sky, seeing, haze, dew and fog, the Moon, satellite passes, Starlink trains, meteor showers, aurora and coming events.', 'tonight'],
  ['eye', 'Best sights tonight', `The clusters, galaxies and nebulae worth finding tonight with your eyes, binoculars or a telescope, allowing for your sky and the Moon, with photos. In ${N.tonight}.`, 'tonight'],
  ['play', 'Guided tour and Sky Hunt', `A narrated tour of tonight’s highlights, or a silent challenge to find five things yourself. Both are in ${N.more} and ${N.tonight}.`, 'tonight'],
  ['star', 'Lessons and quizzes', `Short lessons that point at the real sky, such as finding north with the Plough or the Moon’s phases, and a quiz made from whatever is up tonight. In ${N.more}.`, 'learn'],
  ['gallery', N.gallery, `The finest real photographs of space, each with its story and credit: the newest from Webb and Hubble, every planet through the years, the large moons and the dwarf planets, the Sun, comets and the deep sky. In ${N.more}, and in Learn.`, 'gallery'],
  ['pin', 'Where to go tonight', `Public places near you with darker skies, checked against tonight’s cloud and the Moon, with directions. In ${N.more}.`, 'go'],
  ['calendar', 'Sky calendar', `Meteor showers, eclipses and the planets for the year ahead, worked out for your location or any other place, and the evenings to look for the new crescent, with reminders. In ${N.more}.`, 'calendar'],
  ['bell', 'Alerts', `A notification at about 4 pm when tonight looks clear, ten minutes before bright passes of the space station, and before notable sky events, even with Ahillah closed. Turn them on in ${N.settings}.`, 'settings'],
  ['calendar', 'Travel in time', 'Any date you wish, speeded up or reversed, then share a picture or a link to it.', 'time'],
  ['star', 'Arabic names', `The Arabic names of the stars, planets and more, on their cards and in search (${N.settings}). ${N.layers} offers the Arabian star groups and the Moon’s 28 stations (manāzil).`, 'settings'],
  ['camera', 'Zoom into real photographs', 'Zoom in and the sky becomes real survey photographs, with faint Gaia stars; tap anything in them to find out what it is. Planets and the Moon show their surfaces close up.', null],
  ['eye', N.nakedEye, `Choose it in ${N.layers} to see only what your eyes can see from where you are tonight, with names, the brightest first. The ${N.darkCheck} there fits it to your own eyes.`, 'layers'],
  ['eye', 'Choose what is on the sky', `In ${N.layers}, pick any mix of constellations, star names, galaxies, satellites and meteor showers, or Everything.`, 'layers'],
  ['eye', 'Other kinds of light', `The sky in radio waves, microwaves, infrared, hydrogen light and gamma rays. In ${N.layers}.`, 'layers'],
  ['speaker', N.listen, `Tap ${N.listen} on a card to hear a few checked facts about it, or ${N.say} to hear just its name. Ahillah can also say what you point at (${N.settings}).`, null],
  ['nightEye', N.nightVision, `Warms the screen’s colours, as much as you like, so your eyes stay used to the dark. Near the top of ${N.layers}. After dark, Ahillah also dims its own buttons.`, 'layers'],
  ['search', 'Search, by voice too', 'Planets, stars, constellations, galaxies and satellites. Tap the microphone and ask “what’s that bright star in the west?” or “when does the space station pass?”.', 'search'],
  ['check', 'Your sky log', 'Tick off what you have seen, and work through the 110 Messier objects. In Learn.', 'learn'],
];

// ---------- New users ----------

/** Offered once, on the sky, after a new user's first look round. */
export const OFFER = {
  title: 'New to Ahillah?',
  text: 'See what each button does, one at a time. It takes about a minute.',
  yes: WALK.label,
  no: 'Not now',
  /** After Not now: where to find it again. */
  later: `${N.help} is under the ? at the top, whenever you need it.`,
  /** The same, when the ? has given way (a narrow phone): Help is in Settings. */
  laterInSettings: `${N.help} is in ${N.settings}, whenever you need it.`,
};

/** The last step of the welcome. */
export const WELCOME_LINK = 'Show me around first';
